import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DailyChallengeView from './DailyChallengeView';
import LoadingScreen from './LoadingScreen';
import InstructionsModal from './InstructionsModal';
import { StorageManager } from '../utils/storageManager';
import { runStorageMigrationIfNeeded } from '../utils/migrationManager';
import { fixChallengeScore } from '../utils/fixChallengeScore';
import { STORAGE_KEYS } from '../constants';

// Lazy load the CalendarModal after static imports
const CalendarModal = lazy(() => import('./CalendarModal'));

// Helper function to validate YYYY-MM-DD format
const isValidDateString = (dateStr: string | undefined): dateStr is string => {
  return typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
};

const GameManager: React.FC = () => {
  // Get date from URL, could be undefined if path is just /
  const { date: urlDate } = useParams<{ date?: string }>();
  const navigate = useNavigate(); // Keep navigate for calendar selection

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [activeChallengeDate, setActiveChallengeDate] = useState<string>('');
  const [isCurrentDaily, setIsCurrentDaily] = useState(true); // New state
  const hasRunFormatCleanup = useRef(false);

  // ADD THIS USEEFFECT HOOK
  useEffect(() => {
    const checkAndFixScores = () => {
      // Prior fix example for 2025-06-13
      const fixFlag0613 = `${STORAGE_KEYS.SCORE_FIX_FLAG_PREFIX}2025-06-13`;
      if (localStorage.getItem(fixFlag0613) !== 'true') {
        const corrections0613 = [
          { roundIndex: 1, correctYear: 1929 },
        ];
        const wasFixed0613 = fixChallengeScore('2025-06-13', corrections0613);
        localStorage.setItem(fixFlag0613, 'true');
        if (wasFixed0613) {
          window.location.reload();
          return;
        }
      }

      // One-time correction hook for 2025-08-07 round 5 (index 4)
      // Hardcode correct year = 1935
      const fixFlag0807 = `${STORAGE_KEYS.SCORE_FIX_FLAG_PREFIX}2025-08-07`;
      if (localStorage.getItem(fixFlag0807) !== 'true') {
        const wasFixed0807 = fixChallengeScore('2025-08-07', [
          { roundIndex: 4, correctYear: 1935 },
        ]);
        localStorage.setItem(fixFlag0807, 'true');
        if (wasFixed0807) {
          window.location.reload();
          return;
        }
      }
    };

    checkAndFixScores();
  }, []); // Empty dependency array means this runs once on component mount

  // Effect 1: Determine Active Date and isCurrentDaily based on URL, handle future dates
  useEffect(() => {
    setIsLoading(true);

    const todayET = StorageManager.getTodayDateET();
    let dateToLoad = todayET;
    let isActuallyCurrentDaily = true;

    if (urlDate) {
      if (!isValidDateString(urlDate)) {
        navigate('/', { replace: true });
        return;
      }

      if (urlDate > todayET) {
        navigate('/', { replace: true });
        return;
      } else {
        dateToLoad = urlDate;
        isActuallyCurrentDaily = false;
      }
    } else {
      isActuallyCurrentDaily = true;
      dateToLoad = todayET;
    }

    setIsCurrentDaily(isActuallyCurrentDaily);
    setActiveChallengeDate(dateToLoad);
    setIsLoading(false);
  }, [urlDate, navigate]);

  // Effect 2: Run Migrations/Cleanups (separated for clarity)
  useEffect(() => {
    try {
      runStorageMigrationIfNeeded();
    } catch (migrationError) {
      // Error handling without console.log
    }

    if (!hasRunFormatCleanup.current) {
      try {
        StorageManager.deduplicateGameHistory();
        hasRunFormatCleanup.current = true;
      } catch (error) {
        // Error handling without console.log
      }
    }
  }, []);

  // Handle date selection from Calendar
  const handleCalendarDateSelection = useCallback(
    (date: string) => {
      setShowCalendarModal(false);

      const todayETDateString = StorageManager.getTodayDateET();
      let targetPath: string;
      if (date === todayETDateString) {
        targetPath = '/';
      } else {
        targetPath = `/challenge/${date}`;
      }

      const currentPath = urlDate ? `/challenge/${urlDate}` : '/';

      if (targetPath !== currentPath) {
        navigate(targetPath);
      }
    },
    [navigate, urlDate]
  );

  // Show Instructions Modal on first visit
  useEffect(() => {
    const hasSeen = localStorage.getItem(STORAGE_KEYS.HAS_SEEN_INSTRUCTIONS);
    if (!hasSeen) {
      setShowInstructions(true);
    }
  }, []);

  const handleHideInstructions = () => {
    setShowInstructions(false);
    localStorage.setItem(STORAGE_KEYS.HAS_SEEN_INSTRUCTIONS, 'true');
  };

  const renderContent = () => {
    if (isLoading || !activeChallengeDate) {
      // Check activeChallengeDate is set
      return <LoadingScreen />;
    }
    // Keep this check if fetchAvailableDates is implemented later
    // if (!hasChallengeAvailable) {
    //   return <div className="p-4 text-center text-red-500">Challenge not available for this date.</div>;
    // }

    // Render DailyChallengeView once activeChallengeDate is determined
    return (
      <DailyChallengeView
        key={activeChallengeDate} // Use date as key to force remount on date change
        selectedDate={activeChallengeDate}
        isCurrentDaily={isCurrentDaily} // Pass the new prop
        onShowInstructions={() => setShowInstructions(true)}
        onShowCalendar={() => setShowCalendarModal(true)}
      />
    );
  };

  return (
    <div className="relative h-full">
      {renderContent()}

      {/* Wrap CalendarModal with Suspense */}
      {showCalendarModal && (
        <Suspense fallback={<div>Loading Calendar...</div>}>
          <CalendarModal
            isOpen={showCalendarModal}
            onClose={() => setShowCalendarModal(false)}
            onSelectDate={handleCalendarDateSelection}
            selectedDate={activeChallengeDate}
          />
        </Suspense>
      )}

      <InstructionsModal
        isOpen={showInstructions}
        onClose={handleHideInstructions}
      />
    </div>
  );
};

export default GameManager;
